import { useState, useEffect, useRef } from 'react';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [scrollRotation, setScrollRotation] = useState(0);
  const [stats, setStats] = useState({
    artists: 0,
    domestic: 0,
    year: 2023
  });
  const statsRef = useRef<HTMLDivElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Typing animation states
  const [displayText, setDisplayText] = useState('');
  const [displaySubtext, setDisplaySubtext] = useState('');
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [currentSubtextIndex, setCurrentSubtextIndex] = useState(0);
  const [showCursor, setShowCursor] = useState(true);

  // Section visibility state
  const [visibleSections, setVisibleSections] = useState<Record<string, boolean>>({});
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  const heroSlides = [
    {
      title: "Game & Webtoon",
      subtitle: "Source Development",
      description: "2023년부터 게임 리소스 제작 디자이너와 웹툰 소스 개발 디자이너들과 한길만 걸어온 작가 매칭 글로벌 컴퍼니 빌더",
      image: "https://public.readdy.ai/ai/img_res/721785068b214f91a99858bafdc9d558.jpg"
    }
  ];

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Typing animation effect
  useEffect(() => {
    const title = heroSlides[0].title;
    const subtitle = heroSlides[0].subtitle;
    
    if (currentTextIndex < title.length) {
      const timer = setTimeout(() => {
        setDisplayText(title.slice(0, currentTextIndex + 1));
        setCurrentTextIndex(currentTextIndex + 1);
      }, 100);
      return () => clearTimeout(timer);
    } else if (currentSubtextIndex < subtitle.length) {
      const timer = setTimeout(() => {
        setDisplaySubtext(subtitle.slice(0, currentSubtextIndex + 1));
        setCurrentSubtextIndex(currentSubtextIndex + 1);
      }, 80);
      return () => clearTimeout(timer);
    }
  }, [currentTextIndex, currentSubtextIndex]);

  // Cursor blinking effect
  useEffect(() => {
    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);
    return () => clearInterval(cursorTimer);
  }, []);

  // Scroll rotation effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrollRotation(scrollY * 0.1);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll animation observer
  useEffect(() => {
    const observerOptions = {
      threshold: 0.15,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.getAttribute('data-section');
          if (sectionId) {
            setVisibleSections((prev) => ({ ...prev, [sectionId]: true }));
          }
        }
      });
    }, observerOptions);

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Stats counter animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          
          // Animate artists count
          let artistCount = 0;
          const artistInterval = setInterval(() => {
            artistCount += 10;
            if (artistCount >= 600) {
              setStats(prev => ({ ...prev, artists: 600 }));
              clearInterval(artistInterval);
            } else {
              setStats(prev => ({ ...prev, artists: artistCount }));
            }
          }, 20);

          // Animate domestic count
          let domesticCount = 0;
          const domesticInterval = setInterval(() => {
            domesticCount += 2;
            if (domesticCount >= 100) {
              setStats(prev => ({ ...prev, domestic: 100 }));
              clearInterval(domesticInterval);
            } else {
              setStats(prev => ({ ...prev, domestic: domesticCount }));
            }
          }, 20);
        }
      },
      { threshold: 0.5 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-sm z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-center">
            <a href="/" className="cursor-pointer">
              <img 
                alt="어드밴스" 
                className="h-8 w-auto object-contain" 
                src="https://static.readdy.ai/image/71503c3d7482a205559d38205fea6d8a/69154d005599712b264f01d6b9940bed.png"
              />
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-white">
        {/* Bouncing glow effects */}
        <div className="absolute inset-0 flex items-center justify-center">
          {/* Glow 1 - Red */}
          <div 
            className="absolute w-[500px] h-[500px] bg-gradient-to-br from-red-500 to-red-600 rounded-full blur-3xl opacity-25"
            style={{ animation: 'bounce1 8s ease-in-out infinite' }}
          ></div>
          
          {/* Glow 2 - Pink */}
          <div 
            className="absolute w-[400px] h-[400px] bg-gradient-to-br from-pink-500 to-pink-600 rounded-full blur-3xl opacity-25"
            style={{ animation: 'bounce2 10s ease-in-out infinite' }}
          ></div>
          
          {/* Glow 3 - Purple */}
          <div 
            className="absolute w-[350px] h-[350px] bg-gradient-to-br from-purple-500 to-purple-600 rounded-full blur-3xl opacity-25"
            style={{ animation: 'bounce3 12s ease-in-out infinite' }}
          ></div>
          
          {/* Glow 4 - Blue */}
          <div 
            className="absolute w-[450px] h-[450px] bg-gradient-to-br from-blue-500 to-blue-600 rounded-full blur-3xl opacity-25"
            style={{ animation: 'bounce4 9s ease-in-out infinite' }}
          ></div>
          
          {/* Glow 5 - Cyan */}
          <div 
            className="absolute w-[380px] h-[380px] bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full blur-3xl opacity-25"
            style={{ animation: 'bounce5 11s ease-in-out infinite' }}
          ></div>

          {/* Glow 6 - Green */}
          <div 
            className="absolute w-[300px] h-[300px] bg-gradient-to-br from-green-500 to-green-600 rounded-full blur-3xl opacity-25"
            style={{ animation: 'bounce6 13s ease-in-out infinite' }}
          ></div>

          {/* Glow 7 - Lime */}
          <div 
            className="absolute w-[320px] h-[320px] bg-gradient-to-br from-lime-500 to-lime-600 rounded-full blur-3xl opacity-25"
            style={{ animation: 'bounce7 14s ease-in-out infinite' }}
          ></div>
        </div>
        
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-6 sm:mb-8 tracking-tight">
            <span className="block text-gray-900">
              {displayText}
              {currentTextIndex < heroSlides[0].title.length && showCursor && (
                <span className="inline-block w-1 h-[0.8em] bg-gray-900 ml-1 animate-pulse"></span>
              )}
            </span>
            <span className="block text-gray-900 mt-2 sm:mt-4">
              {displaySubtext}
              {currentTextIndex >= heroSlides[0].title.length && currentSubtextIndex < heroSlides[0].subtitle.length && showCursor && (
                <span className="inline-block w-1 h-[0.8em] bg-gray-900 ml-1 animate-pulse"></span>
              )}
            </span>
          </h1>
          <div className={`transition-all duration-1000 ${currentSubtextIndex >= heroSlides[0].subtitle.length ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4">
              {heroSlides[0].description}
            </p>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 hidden md:block animate-bounce">
          <i className="ri-arrow-down-line text-3xl text-gray-900 opacity-70"></i>
        </div>
      </section>

      {/* About Us & Stats Combined Section */}
      <section 
        id="about" 
        ref={(el) => {
          statsRef.current = el;
          sectionRefs.current['about'] = el;
        }}
        className="py-16 sm:py-24 lg:py-32 bg-white overflow-hidden"
        data-section="about"
      >
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
          visibleSections['about'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
        }`}>
          {/* About Us Content */}
          <div className="mb-16 sm:mb-24 max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 sm:mb-8 text-gray-900 font-serif italic">About Us</h2>
            <div className="space-y-4 sm:space-y-6 text-gray-700 leading-relaxed">
              <p className="text-base sm:text-lg">
                (주) 어드밴스는 2023년 게임소싱 개발을 시작하여 오로지 게임&웹툰 소스 개발이라는 한 길만 걸어온 기업입니다.
              </p>
              <p className="text-base sm:text-lg">
                국내에만 <strong className="text-gray-900 font-semibold">100명의 베테랑 작가</strong>를 보유하고 있으며 대규모 개발의 성공적인 런칭을 위해 미국, 유럽, 아시아 등 <strong className="text-gray-900 font-semibold">글로벌 Full Stack 작가 500명</strong>과 협업하여 가장 안정적이고 최신 기술을 적용합니다.
              </p>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 text-center">
            {[
              { number: `${stats.artists}+`, label: "글로벌 작가 네트워크" },
              { number: `${stats.domestic}+`, label: "국내 베테랑 작가" },
              { number: stats.year, label: "설립 연도" },
              { number: "Global", label: "미국·유럽·아시아 협업" }
            ].map((stat, index) => (
              <div key={index} className="transform hover:scale-110 transition-transform duration-300">
                <div className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 sm:mb-4 font-serif italic text-gray-900">
                  {stat.number}
                </div>
                <p className="text-gray-700 text-sm sm:text-base lg:text-lg">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Builder Section */}
      <section 
        id="services" 
        className="py-16 sm:py-24 lg:py-32 bg-gradient-to-br from-gray-50/50 to-white overflow-hidden"
        ref={(el) => (sectionRefs.current['services'] = el)}
        data-section="services"
      >
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
          visibleSections['services'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
        }`}>
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 text-gray-900 font-serif italic">Company Builder</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed px-4">
              사업 기획부터 운영까지 잘하는 컴퍼니빌더입니다
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-3xl hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-gray-100 cursor-pointer">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-lightbulb-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-900">사업 기획</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                시장 분석부터 비즈니스 모델 설계까지, 성공적인 사업의 시작을 함께합니다.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-3xl hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-gray-100 cursor-pointer">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-rocket-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-900">고도 성장</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                검증된 전략과 글로벌 네트워크로 빠른 성장을 지원합니다.
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 lg:p-10 rounded-3xl hover:shadow-2xl hover:scale-105 transition-all duration-300 border border-gray-100 cursor-pointer">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl flex items-center justify-center mb-4 sm:mb-6">
                <i className="ri-trophy-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-900">EXIT 전략</h3>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                성공적인 엑시트까지 다양한 경험을 바탕으로 최적의 전략을 제시합니다.
              </p>
            </div>
          </div>

          <div className="mt-12 sm:mt-16 text-center px-4">
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed max-w-4xl mx-auto">
              사업 계획부터 고도 성장 그리고 EXIT까지 다양하게 경험한 우리는<br className="hidden sm:block" />
              지금도 새로운 도전을 하고 있습니다.
            </p>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section 
        id="reviews" 
        className="py-16 sm:py-24 lg:py-32 bg-white overflow-hidden"
        ref={(el) => (sectionRefs.current['reviews'] = el)}
        data-section="reviews"
      >
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
          visibleSections['reviews'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
        }`}>
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 text-gray-900 font-serif italic">Reviews</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 px-4">
              우리와 함께하는 파트너의 긍정적인 리뷰를 확인하세요
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                rating: 5,
                text: "어드밴스 협약 작가와의 기억은 새로웠습니다. 그 동안 일했던 대부분의 소싱 회사들은 작품 리테이가 늘어나면 항상 비용을 요구하기 바빴습니다. 그런데 어드밴스는 더 많은 아이디어를 제공하고 진심으로 잘 되길 바랬습니다.",
                author: "김OO 대표",
                company: "게임 개발사"
              },
              {
                rating: 5,
                text: "25년 상반기 쯤인것 같습니다.. 우리 회사의 레벨 밸런싱 시스템 구축을 위해 그 때의 어드밴스 작가님께 개발을 의뢰했습니다. 견적도 만족 스러웠지만 그 결과물은 지금까지도 사용하고 있습니다.",
                author: "박OO 팀장",
                company: "모바일 게임사"
              },
              {
                rating: 5,
                text: "어드밴스는 오래 전부터 맞춤형 작가 섭취에 독보적인 기술을 가지고 있다고 소문이 나 있었습니다. 다양한 장르의 그래픽소스 제공 서비스 경험, 엔진 연동형 소스, 안정화 된 시스템은 우리의 기반이 되었습니다.",
                author: "이OO CTO",
                company: "웹툰 플랫폼"
              }
            ].map((review, index) => (
              <div key={index} className="bg-white p-6 sm:p-8 lg:p-10 rounded-3xl border border-gray-100 hover:-translate-y-2 hover:shadow-xl transition-all duration-300">
                <div className="flex gap-1 mb-4 sm:mb-6">
                  {[...Array(review.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-xl sm:text-2xl text-gray-900"></i>
                  ))}
                </div>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 sm:mb-6">
                  "{review.text}"
                </p>
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                  <div>
                    <p className="text-sm sm:text-base font-semibold text-gray-900">{review.author}</p>
                    <p className="text-xs sm:text-sm text-gray-600">{review.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Game Portfolio Section */}
      <section 
        id="portfolio" 
        className="py-16 sm:py-24 lg:py-32 bg-gradient-to-br from-gray-50/50 to-white overflow-hidden"
        ref={(el) => (sectionRefs.current['portfolio'] = el)}
        data-section="portfolio"
      >
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
          visibleSections['portfolio'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
        }`}>
          <div className="text-center mb-12 sm:mb-20">
            <div className="text-[11px] tracking-[0.3em] uppercase text-gray-600 mb-4 sm:mb-6">Game Portfolio</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6">
              게임<br />
              <span className="font-serif italic font-normal text-gray-900">프로젝트</span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto px-4">
              다양한 장르의 게임 개발 프로젝트를 소개합니다.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                title: "영웅의 귀환 (Hero's Return)",
                description: "중세 판타지 세계관의 대규모 MMORPG. 길드 전쟁과 레이드 콘텐츠가 핵심입니다.",
                tags: ["MMORPG", "판타지", "멀티플레이"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=epic%20medieval%20fantasy%20MMORPG%20game%20scene%20with%20massive%20castle%20fortress%20guild%20war%20battle%20knights%20and%20warriors%20dramatic%20lighting%20professional%20game%20screenshot%20cinematic%20atmosphere%20vibrant%20fantasy%20colors&width=600&height=400&seq=newgame001&orientation=landscape"
              },
              {
                title: "네온 레이서 (Neon Racer)",
                description: "사이버펑크 도시를 배경으로 한 미래형 레이싱 게임. 화려한 네온 효과와 빠른 속도감.",
                tags: ["레이싱", "사이버펑크", "액션"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=futuristic%20cyberpunk%20racing%20game%20neon%20city%20streets%20at%20night%20glowing%20vehicles%20motion%20blur%20high%20speed%20action%20professional%20game%20graphics%20vibrant%20neon%20colors%20purple%20and%20blue%20lighting&width=600&height=400&seq=newgame002&orientation=landscape"
              },
              {
                title: "마법학교 이야기 (Magic Academy)",
                description: "마법학교를 배경으로 한 RPG 어드벤처. 다양한 마법 수업과 친구들과의 우정.",
                tags: ["RPG", "어드벤처", "판타지"],
                year: "2023",
                image: "https://readdy.ai/api/search-image?query=magical%20academy%20RPG%20game%20scene%20beautiful%20fantasy%20school%20castle%20students%20learning%20magic%20spells%20colorful%20magical%20effects%20professional%20game%20art%20warm%20lighting%20enchanting%20atmosphere&width=600&height=400&seq=newgame003&orientation=landscape"
              },
              {
                title: "좀비 서바이벌 (Zombie Survival)",
                description: "포스트 아포칼립스 세계에서 살아남기. 자원 수집과 기지 건설이 중요합니다.",
                tags: ["서바이벌", "호러", "액션"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=post%20apocalyptic%20zombie%20survival%20game%20abandoned%20city%20ruins%20survivors%20fighting%20zombies%20dark%20atmospheric%20lighting%20professional%20game%20screenshot%20intense%20action%20horror%20atmosphere&width=600&height=400&seq=newgame004&orientation=landscape"
              },
              {
                title: "요리왕 대결 (Cooking Master)",
                description: "실시간 요리 대결 게임. 다양한 레시피와 타이머 챌린지로 긴장감 넘치는 플레이.",
                tags: ["시뮬레이션", "캐주얼", "경쟁"],
                year: "2023",
                image: "https://readdy.ai/api/search-image?query=colorful%20cooking%20competition%20game%20scene%20professional%20kitchen%20chef%20preparing%20delicious%20dishes%20timer%20challenge%20vibrant%20food%20graphics%20casual%20game%20art%20style%20cheerful%20atmosphere&width=600&height=400&seq=newgame005&orientation=landscape"
              },
              {
                title: "스페이스 커맨더 (Space Commander)",
                description: "우주 함대를 지휘하는 전략 시뮬레이션. 행성 정복과 자원 관리가 핵심.",
                tags: ["전략", "시뮬레이션", "SF"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=space%20strategy%20game%20massive%20fleet%20battle%20starships%20in%20deep%20space%20nebula%20background%20tactical%20warfare%20professional%20game%20graphics%20epic%20sci-fi%20atmosphere%20colorful%20cosmic%20scene&width=600&height=400&seq=newgame006&orientation=landscape"
              },
              {
                title: "던전 탐험대 (Dungeon Explorers)",
                description: "협동 플레이 던전 크롤러. 친구들과 함께 보물을 찾고 몬스터를 물리치세요.",
                tags: ["협동", "RPG", "던전"],
                year: "2023",
                image: "https://readdy.ai/api/search-image?query=cooperative%20dungeon%20crawler%20game%20party%20of%20adventurers%20exploring%20dark%20mysterious%20dungeon%20treasure%20chests%20monsters%20professional%20game%20art%20atmospheric%20lighting%20fantasy%20RPG%20style&width=600&height=400&seq=newgame007&orientation=landscape"
              },
              {
                title: "농장 이야기 (Farm Story)",
                description: "평화로운 농장 경영 시뮬레이션. 작물 재배, 동물 키우기, 마을 사람들과의 교류.",
                tags: ["시뮬레이션", "힐링", "캐주얼"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=peaceful%20farm%20simulation%20game%20beautiful%20countryside%20colorful%20crops%20cute%20animals%20cozy%20village%20atmosphere%20professional%20casual%20game%20graphics%20warm%20sunny%20lighting%20relaxing%20scene&width=600&height=400&seq=newgame008&orientation=landscape"
              }
            ].map((project, index) => (
              <article key={index} className="group cursor-pointer">
                <div className="mb-4 overflow-hidden rounded-2xl bg-gray-100">
                  <img
                    alt={project.title}
                    className="w-full h-[200px] sm:h-[240px] object-cover transition-transform duration-700 group-hover:scale-110"
                    src={project.image}
                  />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-block px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">게임</span>
                  <span className="text-xs text-gray-500">{project.year}</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 group-hover:text-gray-700 transition-colors">
                  {project.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">{project.description}</p>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, tagIndex) => (
                    <span key={tagIndex} className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Webtoon Portfolio Section */}
      <section 
        className="py-16 sm:py-24 lg:py-32 bg-white overflow-hidden"
        ref={(el) => (sectionRefs.current['webtoon'] = el)}
        data-section="webtoon"
      >
        <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-1000 ${
          visibleSections['webtoon'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
        }`}>
          <div className="text-center mb-12 sm:mb-20">
            <div className="text-[11px] tracking-[0.3em] uppercase text-gray-600 mb-4 sm:mb-6">Webtoon Portfolio</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6">
              웹툰<br />
              <span className="font-serif italic font-normal text-gray-900">프로젝트</span>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto px-4">
              감동과 재미를 선사하는 웹툰 프로젝트를 소개합니다.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                title: "별이 빛나는 밤 (Starlit Night)",
                description: "천문학자와 화가의 로맨스. 별이 가득한 밤하늘 아래 펼쳐지는 아름다운 사랑 이야기.",
                tags: ["로맨스", "힐링", "감성"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=romantic%20korean%20webtoon%20illustration%20couple%20under%20starry%20night%20sky%20telescope%20beautiful%20night%20scene%20emotional%20love%20story%20professional%20digital%20art%20warm%20lighting%20dreamy%20atmosphere&width=600&height=400&seq=newwebtoon001&orientation=landscape"
              },
              {
                title: "검은 늑대 (Black Wolf)",
                description: "현대 도시에 나타난 늑대인간의 비밀. 액션과 미스터리가 가득한 다크 판타지.",
                tags: ["판타지", "액션", "미스터리"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=dark%20fantasy%20webtoon%20illustration%20mysterious%20werewolf%20character%20urban%20city%20night%20dramatic%20shadows%20action%20scene%20professional%20korean%20manhwa%20art%20style%20intense%20atmosphere&width=600&height=400&seq=newwebtoon002&orientation=landscape"
              },
              {
                title: "우리 동네 빵집 (Our Neighborhood Bakery)",
                description: "작은 동네 빵집에서 벌어지는 따뜻한 일상. 빵 향기와 함께하는 힐링 스토리.",
                tags: ["일상", "힐링", "드라마"],
                year: "2023",
                image: "https://readdy.ai/api/search-image?query=cozy%20korean%20bakery%20webtoon%20illustration%20warm%20neighborhood%20bakery%20shop%20delicious%20bread%20pastries%20heartwarming%20daily%20life%20professional%20digital%20art%20soft%20lighting%20comfortable%20atmosphere&width=600&height=400&seq=newwebtoon003&orientation=landscape"
              },
              {
                title: "레벨업 헌터 (Level Up Hunter)",
                description: "게임처럼 레벨업하는 현실. 몬스터를 사냥하며 최강 헌터로 성장하는 이야기.",
                tags: ["액션", "판타지", "게임"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=action%20webtoon%20illustration%20powerful%20hunter%20fighting%20monsters%20level%20up%20system%20game-like%20interface%20dynamic%20battle%20scene%20professional%20korean%20manhwa%20style%20intense%20action%20vibrant%20effects&width=600&height=400&seq=newwebtoon004&orientation=landscape"
              },
              {
                title: "첫사랑 다이어리 (First Love Diary)",
                description: "고등학생의 풋풋한 첫사랑 이야기. 설렘과 아픔이 공존하는 청춘 로맨스.",
                tags: ["로맨스", "학원", "청춘"],
                year: "2023",
                image: "https://readdy.ai/api/search-image?query=high%20school%20romance%20webtoon%20illustration%20korean%20students%20first%20love%20school%20uniform%20cherry%20blossoms%20youthful%20emotions%20professional%20digital%20art%20bright%20colors%20sweet%20atmosphere&width=600&height=400&seq=newwebtoon005&orientation=landscape"
              },
              {
                title: "마왕의 요리사 (Demon King's Chef)",
                description: "마왕성에 취직한 인간 요리사의 코미디 판타지. 마족들을 위한 특별한 요리.",
                tags: ["판타지", "코미디", "요리"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=fantasy%20comedy%20webtoon%20illustration%20human%20chef%20cooking%20in%20demon%20castle%20funny%20monsters%20eating%20delicious%20food%20professional%20korean%20manhwa%20art%20colorful%20humorous%20atmosphere&width=600&height=400&seq=newwebtoon006&orientation=landscape"
              },
              {
                title: "회귀한 검성 (Regressed Sword Master)",
                description: "과거로 돌아간 최강 검사. 모든 것을 바꾸기 위한 두 번째 인생의 시작.",
                tags: ["판타지", "액션", "회귀"],
                year: "2023",
                image: "https://readdy.ai/api/search-image?query=epic%20sword%20master%20webtoon%20illustration%20powerful%20warrior%20with%20legendary%20sword%20time%20regression%20theme%20dramatic%20action%20scene%20professional%20korean%20manhwa%20style%20intense%20lighting%20heroic%20atmosphere&width=600&height=400&seq=newwebtoon007&orientation=landscape"
              },
              {
                title: "비밀의 정원 (Secret Garden)",
                description: "도심 속 숨겨진 마법의 정원. 식물과 대화하는 소녀의 신비로운 모험.",
                tags: ["판타지", "힐링", "모험"],
                year: "2024",
                image: "https://readdy.ai/api/search-image?query=magical%20garden%20webtoon%20illustration%20beautiful%20hidden%20garden%20in%20city%20girl%20talking%20to%20plants%20mystical%20flowers%20professional%20korean%20webtoon%20art%20soft%20lighting%20enchanting%20peaceful%20atmosphere&width=600&height=400&seq=newwebtoon008&orientation=landscape"
              }
            ].map((project, index) => (
              <article key={index} className="group cursor-pointer">
                <div className="mb-4 overflow-hidden rounded-2xl bg-gray-100">
                  <img
                    alt={project.title}
                    className="w-full h-[200px] sm:h-[240px] object-cover transition-transform duration-700 group-hover:scale-110"
                    src={project.image}
                  />
                </div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="inline-block px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">웹툰</span>
                  <span className="text-xs text-gray-500">{project.year}</span>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold mb-2 group-hover:text-gray-700 transition-colors">
                  {project.title}
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed mb-3">{project.description}</p>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, tagIndex) => (
                    <span key={tagIndex} className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-gray-50/50 to-white text-gray-800 px-4 sm:px-6 lg:px-8 py-8 sm:py-10 lg:py-12 border-t border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-left mb-6 sm:mb-8">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4 text-gray-900">
              어드밴스
            </h3>
          </div>
          
          <div className="text-left mb-6 sm:mb-8">
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed mb-3">
              2023년부터 게임소싱 개발을 시작하여 오로지 게임&웹툰 소스 개발이라는 한 길만 걸어온 글로벌 컴퍼니빌더
            </p>
            <div className="text-xs sm:text-sm text-gray-600 space-y-1">
              <p>대표자: 김정헌 | 사업자등록번호: 125-51-00975 | 대표 연락처: 010-7410-3525</p>
              <p>서울특별시 동대문구 장한로14길 81, 105동 704호 (장안동, 래미안 장안아파트)</p>
            </div>
          </div>

          <div className="pt-4 sm:pt-6 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs sm:text-sm text-gray-500">
            <p className="text-center sm:text-left">© 2024 어드밴스 Studio. All rights reserved.</p>
            <a
              href="https://readdy.ai/?ref=logo"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-900 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

<style jsx>{`
  @keyframes float3d1 {
    0%, 100% {
      transform: translateY(0px) translateZ(0px) rotateX(0deg) rotateY(0deg);
    }
    33% {
      transform: translateY(-15px) translateZ(20px) rotateX(10deg) rotateY(15deg);
    }
    66% {
      transform: translateY(10px) translateZ(-15px) rotateX(-8deg) rotateY(-10deg);
    }
  }

  @keyframes rotate3d1 {
    0% {
      transform: rotateX(0deg) rotateY(0deg) rotateZ(0deg) translateZ(0px);
    }
    25% {
      transform: rotateX(90deg) rotateY(45deg) rotateZ(180deg) translateZ(25px);
    }
    50% {
      transform: rotateX(180deg) rotateY(90deg) rotateZ(360deg) translateZ(0px);
    }
    75% {
      transform: rotateX(270deg) rotateY(135deg) rotateZ(540deg) translateZ(-20px);
    }
    100% {
      transform: rotateX(360deg) rotateY(180deg) rotateZ(720deg) translateZ(0px);
    }
  }

  @keyframes wiggle3d1 {
    0%, 100% {
      transform: rotateX(0deg) rotateY(0deg) translateX(0px) translateZ(0px);
    }
    25% {
      transform: rotateX(15deg) rotateY(-20deg) translateX(-10px) translateZ(15px);
    }
    50% {
      transform: rotateX(-10deg) rotateY(25deg) translateX(15px) translateZ(-10px);
    }
    75% {
      transform: rotateX(20deg) rotateY(-15deg) translateX(-8px) translateZ(20px);
    }
  }
`}</style>
